import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { Lock, Smartphone, ChevronDown } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { logEvent } from '../../cloudwatchLogger';
import { 
  setUpTOTP, 
  updateMFAPreference, 
  verifyTOTPSetup,
  getCurrentUser,
  fetchUserAttributes,
  confirmSignIn,
  fetchMFAPreference,
  updateUserAttribute,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  rememberDevice
} from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import {mfaActiveState} from '../../store/atoms';
import { Alert } from '@mui/material';

const MFASetupFlow = ({setIsAuthenticated}) => {
  const [error, setError] = useState('');
  const [qrCode, setQrCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [isPhoneNumberFocused, setIsPhoneNumberFocused] = useState(false);
  const [isVerificationCodeFocused, setIsVerificationCodeFocused] = useState(false);
  const [isSecurityQuestionFocused, setIsSecurityQuestionFocused] = useState(false);
  const [isSecurityAnswerFocused, setIsSecurityAnswerFocused] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [mfaType, setMfaType] = useState('sms');
  const [isMfaActivated, setIsMfaActivated] = useRecoilState(mfaActiveState);
  const [smsSent, setSmsSent] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurityQuestion, setShowSecurityQuestion] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const trimmedEmail = location.state?.newUser;
  // console.log('this is trimmed email: '+JSON.stringify(trimmedEmail));

  

  const validatePhoneNumber = (phoneNumber) => {
    // First, remove all spaces, parentheses, and hyphens
    const cleaned = phoneNumber.replace(/[\s()-]/g, '');
    
    const regex = /^(\+?1)?[0-9]{10}$/;
    
    if (!regex.test(cleaned)) {
      return false;
    }
    
    const last10 = cleaned.slice(-10);
    const areaCode = last10.slice(0, 3);
    
    if (
      areaCode.startsWith('0') || 
      areaCode.startsWith('1') || 
      areaCode === '555' || 
      last10.slice(3, 6) === '555'
    ) {
      return false;
    }
    
    return true;
  };

  async function isSecurityAnswerEmpty() {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes(user);
      //console.log('User Attributes:', attributes);
  
      const securityAnswer = attributes["custom:SecurityAnswer"];
      //console.log('Security Answer:', securityAnswer);
      if(securityAnswer) {
        //console.log('Security Answer is not empty');
        return false;
      } else {
        //console.log('Security Answer is not empty');
        return true;
      }
      //return !securityAnswer; // Returns true if securityAnswer is empty or undefined
    } catch (error) {
      console.error('Error fetching user attributes:', error);
      logEvent({
        action: 'error fetching user attributes',
        details: `error fetching user attributes[security questions] for user ${trimmedEmail.username}`,
        type: 'user_attributes'
      })
      throw error;
    }
  }
  
  const handleDontAskAgainChange = (e) => {
    setDontAskAgain(e.target.checked);
  };

  async function updateLastLogin() {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        // console.log('No authenticated user found');
        return;
      }
  
      // Get the current timestamp
      const timestamp = new Date().toISOString();
      // console.log('Setting login time to:', timestamp);
  
      // Store the login time in localStorage
      localStorage.setItem('loginTime', timestamp);
  
      // console.log('Last login time stored in localStorage successfully');
    } catch (error) {
      console.error('Error storing last login time:', error);
      if (error instanceof Error) {
        console.error('Error type:', error.constructor.name);
        console.error('Error message:', error.message);
        console.error('Full error:', error);
      }
    }
  }

  const verifyTOTPSetupHandler = async () => {
    try {
      if (!verificationCode) {
      throw new Error("TOTP code is required.");
    }
    // console.log('userCode in verify is : '+userCode);
      await verifyTOTPSetup({ code: verificationCode }); // Assuming newUser is available here
      // console.log('TOTP setup successful!');
      await updateMFAPreference({ totp: 'ENABLED', totp: 'PREFERRED' });
      await updateUserAttribute({ 
        userAttribute: {
          attributeKey: 'custom:mfa_setup_completed',
          value: 'true' 
      }
      
    });

      // console.log('MFA setup marked as completed in user attributes.');
      setIsAuthenticated(true);
      // setTimeout(() => {
      //   navigate('/');
      // }, 2000);
      navigate('/');
       logEvent({
            action: 'totp setup verified',
            details: `totp for user ${trimmedEmail} has been verified successfully`,
            type: 'totp_mfa'
          });
    } catch (error) {
      console.error('Error verifying TOTP setup:', error);
      setError('Error verifying TOTP setup')
      logEvent({
        action: 'totp setup verified failed',
        details: `totp for user ${trimmedEmail} has failed to verify`,
        type: 'totp_mfa'
      });
    }
  };

  async function handleRegularLogin() {
    try {
        //console.log('inside regular login');
        setIsProcessing(true);
        setError('');
        if (!verificationCode) {
          console.error('Verification code is required.');
          return;
        }

     
        const confirmsignRes = await confirmSignIn({
          challengeResponse: verificationCode
        });
        
        await new Promise(resolve => setTimeout(resolve, 500));
        // console.log('MFA confirmed, login successful! : '+JSON.stringify(confirmsignRes));
        if (confirmsignRes['isSignedIn']) {
          await updateUserAttribute({ 
            userAttribute: {
              attributeKey: 'custom:mfa_setup_completed',
              value: 'true' 
            }
          });
          await updateLastLogin();
          const isEmpty = await isSecurityAnswerEmpty();
          if(isEmpty) {
            //console.log('is security ans empty: '+isSecurityAnswerEmpty());
            setShowSecurityQuestion(true);
          } else {
            setIsAuthenticated(true);
            logEvent({
              action: 'login success',
              details: `login for user ${trimmedEmail.username} has been successful`,
              type: 'login'
            });
            // setIsProcessing(false);
            setShowSuccess(true);
            setTimeout(() => {
              navigate('/');
            }, 1500); 
            navigate('/');
          }
          
        }
    } catch (error) {
      console.error('Error during regular login with MFA:', error);
      // logEvent({
      //   action: 'login failed',
      //   details: `login for user (${trimmedEmail.username}) with ${phoneNumber} has failed in regular login ${error.message}`,
      //   type: 'login'
      // });
      setIsProcessing(false);
      setError(`Error during regular login with MFA: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setShowSuccess(false);
    }
  }

  const sendSMSVerification = async () => {
    try {
      //console.log('inside send sms verification');
      if (!validatePhoneNumber(phoneNumber)) {
        logEvent({
          action: 'phone number validation failed',
          details: `phone number validation failed for user ${trimmedEmail} with phone number ${phoneNumber}`,
          type: 'phone_number validation'
        });
        throw new Error('Invalid phone number format');
        
      }
  
      const formattedPhoneNumber = phoneNumber.startsWith('+') 
        ? phoneNumber 
        : `+1${phoneNumber.replace(/\D/g, '')}`;
  
      // Update user phone number attribute
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'phone_number',
          value: formattedPhoneNumber,
        }
      });
  
      
  
      // Optional: Add a small delay to ensure attribute is processed
      await new Promise(resolve => setTimeout(resolve, 500));
  
      // Send verification code to the phone number
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number'
      });
  
      setSmsSent(true);
      setError('');
    } catch (error) {
      console.error('Error sending SMS verification:', error);
      setError(`Error sending SMS verification: ${error.message}`);
      logEvent({
        action: 'error sending sms verification',
        details: `Error sending sms verification for user ${trimmedEmail.username} with phone number ${phoneNumber}`,
        type: 'verification_code'
      });
    }
  };

  const verifySMSSetupHandler = async () => {
    try {
      //console.log('inside verify sms setup');
      if (!verificationCode) {
        // logEvent({
        //   action: 'verification code validation failed',
        //   details: `verification code validation failed for user ${trimmedEmail.username} with phone number ${phoneNumber}: verification code is required`,
        //   type: 'phone_number validation'
        // });
        throw new Error("Verification code is required.");
      }

      //console.log('verfiy sms setup '+verificationCode );

      // Confirm the phone number with the verification code
      // await confirmUserAttribute({
      //   attributeKey: 'phone_number',
      //   confirmationCode: verificationCode
      // });
      
      const userAttributeKey = 'phone_number';
      try {
        await confirmUserAttribute({ userAttributeKey, confirmationCode: verificationCode });
      } catch (error) {
        // console.log(error);
        setError('Invalid verification code');
        // logEvent({
        //   action: 'invalid verification code',
        //   details: `invalid verification code for user ${trimmedEmail} with phone number: ${phoneNumber}`,
        //   type: 'verification_code'
        // });
        return;
      }

      // Update MFA preference to use SMS
      await updateMFAPreference({ sms: 'PREFERRED' });
      console.log('prefered sms');
      // Mark MFA setup as completed
      await updateUserAttribute({ 
        userAttribute: {
          attributeKey: 'custom:mfa_setup_completed',
          value: 'true' 
        }
      });

      // if (dontAskAgain) {
      //   await rememberDevice(); // Mark the device as trusted
      //   console.log('Device remembered');
      // }

      const isEmpty = await isSecurityAnswerEmpty();
      if(isEmpty) {
        setShowSecurityQuestion(true);
      } else {
        setIsAuthenticated(true);
        logEvent({
          action: 'verfication code verified',
          details: `verification code for user ${trimmedEmail.username} with phone number: ${phoneNumber} has been verified successfully`,
          type: 'verification_code'
        });
        navigate('/');
      }
      
      
    } catch (error) {
      console.error('Error verifying SMS setup:', error);
      setError('Error verifying SMS setup:', error)
      // logEvent({
      //   action: 'verfication code verification failed', 
      //   details: `verification code for user ${trimmedEmail.username} with phone number: ${userAttributeKey} has failed to verify ${error.message}`,
      //   type: 'verification_code'
      // });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isMfaActivated) {
      if (mfaType === 'mfaApp') {
        //console.log('totp setup handler');
        await verifyTOTPSetupHandler();
      } else if (mfaType === 'sms') {
        if (!smsSent) {
          //console.log('sms setup handler');
          await sendSMSVerification();
        } else {
          //console.log('verify sms setup handler');
          await verifySMSSetupHandler();
        }
      }
    } else {
      //console.log('into the regular login');
      await handleRegularLogin();
    }
  };

  const handleSaveSecurityQuestion = async (e) => {
    e.preventDefault();
    // Store the security question and answer as custom attributes
    await updateUserAttribute({
      userAttribute: {
        attributeKey: 'custom:SecurityQuestion', // Ensure this matches the custom attribute in Cognito
        value: securityQuestion,
      }
    });

    await updateUserAttribute({
      userAttribute: {
        attributeKey: 'custom:SecurityAnswer', // Ensure this matches the custom attribute in Cognito
        value: securityAnswer,
      }
    });
    setIsAuthenticated(true);
    navigate('/');    
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   // console.log('usercode is: '+userCode);
  //   if(!isMfaActivated) {
  //     if(mfaType === 'mfaApp') {
  //       verifyTOTPSetupHandler();
  //     } else {

  //     }  
  //   } else {
  //     handleRegularLogin();
  //   }
  // };

  return (
    <div className="flex flex-col md:flex-row w-full">
      <div className="w-full md:w-1/2 bg-gray-100 flex items-center justify-center min-h-screen">
        <div className="flex w-[22.75rem] flex-col items-center gap-[3.75rem] flex-shrink-0">
          {/* Main Container */}
          <div className="flex flex-col items-start gap-[0.625rem] self-stretch">
            {/* Conditional rendering for the heading */}
            {showSecurityQuestion ? (
              <>
                <p className="text-black font-inter text-[3.3125rem] font-black leading-[120%]">
                  Account Recovery Options
                </p>
                <p className="text-black font-inter text-[1rem] font-normal leading-[120%]">
                  If you forgot your password or cannot access your account, we will use this information to help you get back in.
                </p>
              </>
            ) : (
              <p className="text-black font-inter text-[3.8125rem] font-black leading-[120%]">
                Multi-factor Authentication
              </p>
            )}
          </div>
  
        {mfaType === 'sms' && 
          <div className="flex flex-col items-center gap-[1.25rem] self-stretch">
          {showSecurityQuestion ? (
            <form onSubmit={handleSaveSecurityQuestion} className="flex flex-col items-center gap-[3.75rem] w-[22.75rem] flex-shrink-0">
            <div className="flex flex-col gap-[1.25rem] w-full">
              <div className={`flex p-3.5 items-center gap-3.5 self-stretch rounded-[0.875rem] bg-white border border-solid ${isSecurityQuestionFocused ? 'border-black' : 'border-transparent'}`}>
                <select
                  value={securityQuestion}
                  onChange={(e) => setSecurityQuestion(e.target.value)}
                  onFocus={() => setIsSecurityQuestionFocused(true)}
                  onBlur={() => setIsSecurityQuestionFocused(false)}
                  required
                  className={`w-full text-[16px] font-inter font-normal leading-[120%] focus:outline-none ${isSecurityQuestionFocused ? 'placeholder-black' : 'placeholder-[#D9D9D9]'}`}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    color: 'black',
                  }}
                >
                  <option value="" disabled>Select a security question</option>
                  <option value="What's your favorite city?">What's your favorite city?</option>
                  <option value="What's your favorite food?">What's your favorite food?</option>
                  <option value="What was your school name?">What was your school name?</option>
                </select>
              </div>

              <div className={`flex p-3.5 items-center gap-3.5 self-stretch rounded-[0.875rem] bg-white border border-solid ${isSecurityAnswerFocused ? 'border-black' : 'border-transparent'}`}>
                <Lock size={24} />
                <input
                  type="text"
                  placeholder="Your Answer"
                  value={securityAnswer}
                  onFocus={() => setIsSecurityAnswerFocused(true)}
                  onBlur={() => setIsSecurityAnswerFocused(false)}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  required
                  className={`w-full text-[16px] font-inter font-normal leading-[120%] focus:outline-none ${isSecurityAnswerFocused ? 'placeholder-black' : 'placeholder-[#D9D9D9]'}`}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    color: 'black',
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              className="flex w-full py-[1.25rem] px-[0.625rem] justify-center items-center gap-2.5 rounded-[0.875rem] bg-[#EBAE12] text-white font-bold text-[16px] hover:bg-[#D49D10] transition duration-300 relative"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Save Security Question
            </button>
          </form>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col items-center gap-[3.75rem] w-[22.75rem] flex-shrink-0">
            <div className="flex flex-col gap-[1.25rem] w-full">
              {error && error.length > 0 && <Alert severity="error">{error}</Alert>}
        
              {!isMfaActivated && !smsSent ? (
                <>
                  {/* Phone Number Input */}
                  <div className={`flex p-3.5 items-center gap-3.5 self-stretch rounded-[0.875rem] bg-white border border-solid ${isPhoneNumberFocused ? 'border-black' : 'border-transparent'}`}>
                    <Smartphone size={24} />
                    <input
                      type="tel"
                      placeholder="Phone Number (e.g. +1234567890)"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      onFocus={() => setIsPhoneNumberFocused(true)}
                      onBlur={() => setIsPhoneNumberFocused(false)}
                      required
                      className={`w-full text-[16px] font-inter font-normal leading-[120%] focus:outline-none ${isPhoneNumberFocused ? 'placeholder-black' : 'placeholder-[#D9D9D9]'} ${error ? 'text-[#EF6950]' : ''}`}
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        color: error ? '#EF6950' : 'black',
                      }}
                    />
                  </div>
                  {/* Don't Ask Again Checkbox */}
                  {/* <div className="flex items-center justify-start gap-2.5">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="dontAskAgain"
                        checked={dontAskAgain}
                        onChange={handleDontAskAgainChange}
                        className="w-[19px] h-[19px] flex-shrink-0 appearance-none bg-gray-300 border-2 border-gray-400 rounded-sm checked:bg-gray-300 checked:border-gray-400 focus:outline-none transition duration-200"
                      />
                      <svg
                        className={`absolute left-0 top-0 w-[19px] h-[19px] pointer-events-none ${dontAskAgain ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}  
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="black"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <label htmlFor="dontAskAgain" className="text-[16px] font-inter font-normal leading-[120%] text-black" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Don't ask again on this device
                    </label>
                  </div> */}
                </>
              ) : (
                <div className={`flex p-3.5 items-center gap-3.5 self-stretch rounded-[0.875rem] bg-white border border-solid ${isVerificationCodeFocused ? 'border-black' : 'border-transparent'}`}>
                  <Lock size={24} />
                  <input
                    type="text"
                    placeholder="Verification Code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    onFocus={() => setIsVerificationCodeFocused(true)}
                    onBlur={() => setIsVerificationCodeFocused(false)}
                    required
                    className={`w-full text-[16px] font-inter font-normal leading-[120%] focus:outline-none ${isVerificationCodeFocused ? 'placeholder-black' : 'placeholder-[#D9D9D9]'} ${error ? 'text-[#EF6950]' : ''}`}
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      color: error ? '#EF6950' : 'black',
                    }}
                  />
                </div>
              )}
            </div>
        
            {/* <button
              type="submit"
              className="flex w-full py-[1.25rem] px-[0.625rem] justify-center items-center gap-2.5 rounded-[0.875rem] bg-[#EBAE12] text-white font-bold text-[16px] hover:bg-[#D49D10] transition duration-300 relative"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {!isMfaActivated && !smsSent ? 'Send Verification Code' : 'Verify'}
            </button> */}
            <button
                  type="submit"
                  className={`flex w-[22.6875rem] py-[1.25rem] px-[0.625rem] justify-center items-center gap-2.5 rounded-[0.875rem] bg-[#EBAE12] text-white font-bold text-[16px] hover:bg-[#D49D10] transition duration-300 relative ${
                    isProcessing ? 'cursor-not-allowed' : ''
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  
                  disabled={isProcessing}
                >
                  <span className="flex items-center justify-center">
                    {showSuccess ? (
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    ) : (
                     <> {!isMfaActivated && !smsSent ? 'Send Verification Code' : 'Verify'} </>
                    )}
                    {isProcessing && (
                      <svg className="animate-spin-fast ml-2 h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <g>
                          <line x1="12" y1="2" x2="12" y2="6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="12" y1="18" x2="12" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="2" y1="12" x2="6" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="18" y1="12" x2="22" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        </g>
                      </svg>
                    )}
                  </span>
                </button>
          </form>
          )}
        </div>
        }
      </div>
    </div>
    <div className="hidden md:flex md:w-1/2 bg-white justify-center items-center relative overflow-hidden">
      {/* Logo container */}
      <div className="flex w-[23rem] flex-col items-start gap-5 absolute left-[6.5rem] top-[5rem]">
        <img
          src="Frame8.png"
          alt="FINDRAISER Logo"
          className="w-full h-auto object-contain"
        />
      </div>

      {/* Branding image container */}
      <div className="absolute bottom-0 right-0 w-[250px] h-[250px]">
        <img
          src="Branding.png"
          alt="FINDRAISER Branding"
          className="w-full h-full object-contain md:scale-90 sm:scale-75 lg:scale-100"
          style={{
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        />
      </div>
    </div>
  </div>
  );
};

export default MFASetupFlow;